import React, { useState, useEffect } from 'react';
import Header from './Header.jsx';
import useApiRequest from './useApiRequest.jsx';
import useFetch from './useFetch.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import notify from './Nots.jsx';
import ChangePassword from './ChangePassword.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    User, Mail, Phone, Calendar, MapPin, Camera, 
    Edit, Save, X, Shield, Lock, Eye, EyeOff,
    CheckCircle, AlertCircle, Upload, Download, Settings,
    Bell, CreditCard, Activity, Award, Star, TrendingUp,
    Users, Calendar as CalendarIcon, Heart, Eye as EyeIcon
} from 'lucide-react';

const UserProfile = () => {
    const { setLoading } = useLoadingContext();
    const { putRequest, getRequest } = useApiRequest();
    const [profile, setProfile] = useState(null);
    const [editing, setEditing] = useState(false);
    const [password, setPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        dob: '',
        gender: '',
        contact: '',
        profile: null,
    });
    const [preview, setPreview] = useState(null);

    // Fetch user statistics from backend
    const { data: backendStats, loading: statsLoading } = useFetch(`${domain}/statistics`);
    const { data: userEvents, loading: eventsLoading } = useFetch(`${domain}/my-events`);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const response = await getRequest(`${domain}/accounts/profile`);
                setProfile(response.body);
                setFormData(response.body);
            } catch (error) {
                console.error('Error fetching profile:', error);
                notify("Failed to load profile", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.trim().length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }
        
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        
        if (!formData.contact.trim()) {
            newErrors.contact = 'Contact number is required';
        }
        
        if (!formData.dob) {
            newErrors.dob = 'Date of birth is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            console.log('File selected:', file.name, file.type, file.size);
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                notify("Please select an image file", "error");
                return;
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                notify("Image size should be less than 5MB", "error");
                return;
            }

            setFormData((prev) => ({
                ...prev,
                profile: file,
            }));
            
            console.log('FormData updated with file:', file);

            // Create a preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
                console.log('Preview created');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageClick = () => {
        document.getElementById('profilePicInput').click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setLoading(true);
        const form = new FormData();
        
        // Add all form fields
        for (const key in formData) {
            if (key === 'profile') {
                // Handle profile image file
                if (formData[key] instanceof File) {
                    form.append('profile', formData[key]);
                    console.log('Adding profile file:', formData[key].name, formData[key].size);
                } else if (formData[key] && typeof formData[key] === 'string') {
                    // If it's a string (existing image URL), don't add it to avoid overwriting
                    console.log('Profile is existing URL, skipping:', formData[key]);
                }
            } else {
                // Add all other fields
                form.append(key, formData[key]);
            }
        }

        // Debug: Log what's being sent
        console.log('FormData contents:');
        for (let [key, value] of form.entries()) {
            console.log(key, value);
        }

        try {
            // Ensure the profile file is included if it exists
            if (formData.profile instanceof File) {
                console.log('Sending profile file to backend...');
                console.log('File details:', {
                    name: formData.profile.name,
                    type: formData.profile.type,
                    size: formData.profile.size
                });
            }
            
            const response = await putRequest(`${domain}/accounts/profile`, form, true);
            console.log('Backend response:', response);

            if (response.body.status === "ok") {
                notify("Profile updated successfully", "success");
                setProfile(response.body.data);
                setEditing(false);
                // Clear preview after successful update
                setPreview(null);
            } else {
                notify(response.body.data || "Failed to update profile", "error");
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            notify("Failed to update profile", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData(profile);
        setPreview(null);
        setErrors({});
        setEditing(false);
    };

    // Calculate user statistics
    const getUserStats = () => {
        const currentYear = new Date().getFullYear();
        const memberSince = profile?.date_joined ? new Date(profile.date_joined).getFullYear() : currentYear;
        
        return {
            eventsAttended: userEvents?.length || 0,
            memberSince: memberSince,
            rating: backendStats?.average_rating || 4.8,
            totalEvents: backendStats?.total_events || 0,
            totalUsers: backendStats?.total_users || 0,
            totalTicketsSold: backendStats?.total_tickets_sold || 0,
            averageRating: backendStats?.average_rating || 4.8
        };
    };

    if (!profile) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
                <Header />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-teal-500 rounded-full animate-pulse mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    const userStats = getUserStats();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
            <Header />
            
            <div className="container mx-auto px-4 py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-8xl mx-auto"
                >
                    {/* Header with Action Buttons */}
                    <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                            <h1 className="text-lg font-bold text-gray-800 mb-2">My Profile</h1>
                            <p className="text-sm text-gray-600">Manage your account information and preferences</p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                disabled={password}
                                onClick={() => setEditing(!editing)}
                                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                            >
                                {editing ? (
                                    <>
                                        <X className="w-5 h-5" />
                                        Cancel Edit
                                    </>
                                ) : (
                                    <>
                                        <Edit className="w-5 h-5" />
                                        Edit Profile
                                    </>
                                )}
                            </button>
                            
                            <button
                                disabled={editing}
                                onClick={() => setPassword(!password)}
                                className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 !text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                            >
                                {password ? (
                                    <>
                                        <X className="w-5 h-5" />
                                        Cancel Password
                                    </>
                                ) : (
                                    <>
                                        <Lock className="w-5 h-5" />
                                        Change Password
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                        {/* Profile Card */}
                        <div className="xl:col-span-1">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sticky top-8"
                            >
                                {/* Profile Picture */}
                                <div className="text-center mb-8">
                                    <div className="relative inline-block group">
                                        <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-teal-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                            <img
                                src={preview || (profile && domain + profile.profile) || '/profilepics/user.png'}
                                alt="Profile"
                                            className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-2xl cursor-pointer hover:scale-105 transition-all duration-300"
                                onClick={handleImageClick}
                            />
                                        {editing && (
                                            <div 
                                                className="absolute bottom-4 right-4 w-12 h-12 bg-gradient-to-r from-orange-500 to-teal-600 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all duration-200 shadow-lg hover:shadow-xl"
                                                onClick={handleImageClick}
                                                title="Click to change profile picture"
                                            >
                                                <Camera className="w-6 h-6 !text-white" />
                                            </div>
                                        )}
                            </div>
                            <input
                                type="file"
                                id="profilePicInput"
                                        className="hidden"
                                onChange={handleFileChange}
                                        accept="image/*"
                            />
                                    <p className="text-sm text-gray-500 mt-4">
                                        {editing 
                                            ? "Click the image or camera icon to change profile picture" 
                                            : "Click Edit Profile to modify your information"
                                        }
                                    </p>
                                </div>

                                {/* Profile Stats */}
                                <div className="space-y-4 mb-8">
                                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                                                    <Activity className="w-5 h-5 text-orange-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-orange-700 font-medium">Events Attended</p>
                                                    <p className="text-xl font-bold text-orange-800">
                                                        {eventsLoading ? (
                                                            <div className="h-6 bg-orange-200 rounded animate-pulse"></div>
                                                        ) : (
                                                            userStats.eventsAttended
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                                                    <Award className="w-5 h-5 text-teal-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-teal-700 font-medium">Member Since</p>
                                                    <p className="text-xl font-bold text-teal-800">{userStats.memberSince}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-4 border border-orange-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-teal-100 rounded-xl flex items-center justify-center">
                                                    <Star className="w-5 h-5 text-orange-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-700 font-medium">Platform Rating</p>
                                                    <p className="text-xl font-bold text-gray-800">
                                                        {statsLoading ? (
                                                            <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                                                        ) : (
                                                            `${userStats.rating}★`
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Platform Statistics */}
                                <div className="mb-8">
                                    <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-orange-500" />
                                        Platform Stats
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <CalendarIcon className="w-4 h-4 text-orange-500" />
                                                <span className="text-sm text-gray-600">Total Events</span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-800">
                                                {statsLoading ? (
                                                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                                                ) : (
                                                    userStats.totalEvents
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-teal-500" />
                                                <span className="text-sm text-gray-600">Total Users</span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-800">
                                                {statsLoading ? (
                                                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                                                ) : (
                                                    userStats.totalUsers
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <Heart className="w-4 h-4 text-orange-500" />
                                                <span className="text-sm text-gray-600">Tickets Sold</span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-800">
                                                {statsLoading ? (
                                                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                                                ) : (
                                                    userStats.totalTicketsSold
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="space-y-3">
                                    <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-3 group">
                                        <Settings className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                                        Account Settings
                                    </button>
                                    
                                    <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-3 group">
                                        <Bell className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                                        Notifications
                                    </button>
                                    
                                    <button className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-3 group">
                                        <CreditCard className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                                        Payment Methods
                                    </button>
                                </div>
                            </motion.div>
                        </div>

                        {/* Main Content */}
                        <div className="xl:col-span-3">
                            <AnimatePresence mode="wait">
                                {editing ? (
                                    <motion.div
                                        key="edit-form"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
                                    >
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                                                <Edit className="w-6 h-6 !text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800">Edit Profile</h3>
                                                <p className="text-sm text-gray-600">Update your personal information</p>
                        </div>
                    </div>

                                        <form onSubmit={handleSubmit} className="space-y-8">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Username
                                                    </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                                        className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                                            errors.username ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    />
                                                    {errors.username && (
                                                        <p className="text-sm text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.username}
                                                        </p>
                                                    )}
                        </div>

                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Email Address
                                                    </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                                        className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                                            errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    />
                                                    {errors.email && (
                                                        <p className="text-sm text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.email}
                                                        </p>
                                                    )}
                        </div>

                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        First Name
                                                    </label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                                        className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                                            errors.first_name ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    />
                                                    {errors.first_name && (
                                                        <p className="text-sm text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.first_name}
                                                        </p>
                                                    )}
                            </div>

                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Last Name
                                                    </label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                                        className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                                            errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    />
                                                    {errors.last_name && (
                                                        <p className="text-sm text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.last_name}
                                                        </p>
                                                    )}
                            </div>

                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Date of Birth <span className="text-red-500">*</span>
                                                    </label>
                                <input
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    required
                                    className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                        errors.dob ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                    }`}
                                />
                                {errors.dob && (
                                    <p className="text-sm text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" />
                                        {errors.dob}
                                    </p>
                                )}
                            </div>

                                                <div className="space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Gender
                                                    </label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleChange}
                                                        className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200"
                                >
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                                                <div className="lg:col-span-2 space-y-2">
                                                    <label className="block text-sm font-semibold text-gray-700">
                                                        Contact Number
                                                    </label>
                                <input
                                    type="text"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleChange}
                                                        className={`w-full px-3 py-2 text-sm border-2 rounded-xl focus:ring-4 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 ${
                                                            errors.contact ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                                        }`}
                                                    />
                                                    {errors.contact && (
                                                        <p className="text-sm text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="w-4 h-4" />
                                                            {errors.contact}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex gap-4 pt-6 border-t border-gray-200">
                                                <button
                                                    type="submit"
                                                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                                                >
                                                    <Save className="w-5 h-5" />
                                                    Update Profile
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCancel}
                                                    className="px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 transform hover:scale-105"
                                                >
                                                    <X className="w-5 h-5" />
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </motion.div>
                                ) : password ? (
                                    <motion.div
                                        key="password-change"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
                                    >
                                        <ChangePassword />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="profile-info"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="space-y-8"
                                    >
                                        {/* Profile Information */}
                                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                                            <div className="flex items-center gap-4 mb-8">
                                                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                                                    <User className="w-6 h-6 !text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-800">Personal Information</h3>
                                                    <p className="text-sm text-gray-600">Your account details and preferences</p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                <div className="space-y-6">
                                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                                                            <User className="w-5 h-5 text-orange-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500">Username</p>
                                                            <p className="font-semibold text-gray-800 text-sm">{profile.username}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                                                            <Mail className="w-5 h-5 text-teal-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500">Email Address</p>
                                                            <p className="font-semibold text-gray-800 text-sm">{profile.email}</p>
                            </div>
                        </div>

                                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-teal-100 rounded-xl flex items-center justify-center">
                                                            <User className="w-5 h-5 text-orange-600" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500">Full Name</p>
                                                            <p className="font-semibold text-gray-800 text-sm">
                                                                {profile.first_name} {profile.last_name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="space-y-6">
                                                    {profile.contact && (
                                                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                                                                <Phone className="w-5 h-5 text-orange-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Contact Number</p>
                                                                <p className="font-semibold text-gray-800 text-sm">{profile.contact}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {profile.dob && (
                                                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                                                                <Calendar className="w-5 h-5 text-teal-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Date of Birth</p>
                                                                <p className="font-semibold text-gray-800 text-sm">{profile.dob}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {profile.gender && (
                                                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                            <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-teal-100 rounded-xl flex items-center justify-center">
                                                                <User className="w-5 h-5 text-orange-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Gender</p>
                                                                <p className="font-semibold text-gray-800 text-sm capitalize">{profile.gender}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Security Advice */}
                                        <ProfileSecurityAdvice />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

const ProfileSecurityAdvice = () => {
    return (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 !text-white" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-800">Security & Privacy</h3>
                    <p className="text-sm text-gray-600">Keep your account safe and secure</p>
                </div>
            </div>

            <div className="space-y-8">
                <div className="bg-gradient-to-r from-orange-50 via-teal-50 to-orange-50 rounded-2xl p-8 border border-orange-200">
                    <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
                        <Shield className="w-6 h-6 text-orange-600" />
                        Profile Security Best Practices
                    </h4>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <CheckCircle className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Regular Password Updates</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Change your password every 2-3 months using strong, unique passwords with a mix of letters, numbers, and special characters.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <Lock className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Two-Factor Authentication</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Enable 2FA whenever possible to add an extra layer of security to your account.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <AlertCircle className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Beware of Phishing</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Always verify the source before responding to emails or messages requesting personal information.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                <Eye className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                                <h5 className="font-semibold text-gray-800 mb-2">Privacy Settings</h5>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Regularly review and update your privacy settings to control who can see your profile information.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8">
                    <h4 className="text-lg font-bold text-gray-800 mb-6">Additional Security Measures</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                            <span className="text-gray-700">Use a password manager for strong passwords</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                            <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                            <span className="text-gray-700">Never share your login credentials</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                            <span className="text-gray-700">Log out from shared devices</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                            <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                            <span className="text-gray-700">Monitor for suspicious activity</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
